import { useCallback, useEffect, useMemo, useState } from 'react'
import { ReceiptText, ShieldAlert, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import { useGetOwnerStatementIndex, useGetFleetStateSummary } from '../hooks/backend/statements'
import { useCapabilities } from '../hooks/useCapabilities'
import { useIsDesktop } from '../hooks/useIsDesktop'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Panel } from '../components/layout/Panel'
import { FleetStateStrip } from '../components/statements/FleetStateStrip'
import { OwnerStatementList } from '../components/statements/OwnerStatementList'
import { VehicleList } from '../components/statements/VehicleList'
import { VehicleDetail } from '../components/statements/VehicleDetail'
import { StatementMatrix } from '../components/statements/StatementMatrix'
import { EvidenceCoverage } from '../components/statements/EvidenceCoverage'
import {
  type OwnerVehicleRow,
  type OwnerGroup,
  type FleetStateSummary,
} from '../utils/statements'

// Ways to read the same statements. "month" is the fleet-wide matrix: every
// owner's line items for one period, side by side, defaulting to the open month.
// "live" is the same matrix recomputed from the database on every load, with no
// statement adjustments, balances or payouts: the real numbers by month, which
// move the moment an earlier month's shifts or costs change. "owner" is the
// original drill-down: one owner, one vehicle, one period at a time, and still
// the only way to regenerate or send. The choice is remembered because an
// operator settles into one of them.
type StatementView = 'month' | 'live' | 'owner' | 'evidence'
const VIEW_KEY = 'fleetly.ownerStatements.view'

function readView(): StatementView {
  try {
    const raw = localStorage.getItem(VIEW_KEY)
    if (raw === 'month' || raw === 'live' || raw === 'owner' || raw === 'evidence') return raw
  } catch {
    // Storage can be unavailable (private browsing, quota). Not worth surfacing.
  }
  return 'month'
}

function ViewToggle({
  view,
  onChange,
}: {
  view: StatementView
  onChange: (v: StatementView) => void
}) {
  const options: { value: StatementView; label: string }[] = [
    { value: 'month', label: 'All owners, by month' },
    { value: 'live', label: 'Real numbers, live' },
    { value: 'owner', label: 'One owner at a time' },
    { value: 'evidence', label: 'What has no document' },
  ]
  return (
    <div className="inline-flex overflow-hidden rounded-md border border-border">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={cn(
            'min-h-[36px] px-3 text-xs font-medium transition-colors',
            view === option.value
              ? 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]'
              : 'bg-card text-muted-foreground hover:bg-accent',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

export default function OwnerStatements() {
  const { loading: accessLoading, canView, canViewFinance, canEditFleet, caption } = useCapabilities()
  // Only one of the mobile drill-down tree or the desktop panel tree is ever
  // mounted at a time, so panes that fetch their own data (VehicleDetail)
  // never end up double-fetching behind a CSS-hidden copy.
  const isDesktop = useIsDesktop()

  const [view, setView] = useState<StatementView>(readView)

  const index = useGetOwnerStatementIndex()
  const summary = useGetFleetStateSummary()
  const triggerIndex = index.trigger
  const triggerSummary = summary.trigger

  const [search, setSearch] = useState('')
  const [stateFilter, setStateFilter] = useState<string | null>(null)
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null)
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null)
  // Below lg the three panes become a drill-down: owners, then vehicles, then
  // the statement. At lg and above mobileStep is ignored and all three render
  // side by side.
  const [mobileStep, setMobileStep] = useState<'owners' | 'vehicles' | 'statement'>('owners')
  // The fleet state strip is collapsed by default on mobile so it does not eat
  // the viewport before the operator reaches the owner list.
  const [fleetOpen, setFleetOpen] = useState(false)

  const changeView = useCallback((next: StatementView) => {
    setView(next)
    try {
      localStorage.setItem(VIEW_KEY, next)
    } catch {
      // See readView.
    }
  }, [])

  // The owner index and the fleet strip belong to the drill-down. Fetching them
  // while the month matrix is on screen would be two large reads nobody asked
  // for.
  useEffect(() => {
    if (canView && canViewFinance && view === 'owner') {
      triggerIndex({}, { skipCache: true })
      triggerSummary({}, { skipCache: true })
    }
  }, [canView, canViewFinance, view, triggerIndex, triggerSummary])

  const rows = (index.data as OwnerVehicleRow[] | undefined) ?? []
  const fleet = (summary.data as FleetStateSummary | undefined) ?? {
    counts: [],
    reconciliation: [],
    resolutions: [],
  }

  // Apply the operational-state filter at the vehicle level.
  const stateFilteredRows = useMemo(() => {
    if (!stateFilter) return rows
    return rows.filter((r) => r.operational_state === stateFilter)
  }, [rows, stateFilter])

  // Group vehicles by owner.
  const groups = useMemo<OwnerGroup[]>(() => {
    const map = new Map<string, OwnerGroup>()
    for (const r of stateFilteredRows) {
      let g = map.get(r.owner_id)
      if (!g) {
        g = {
          owner_id: r.owner_id,
          owner_name: r.owner_name,
          owner_email: r.owner_email,
          owner_status: r.owner_status,
          vehicles: [],
          vehicle_count: 0,
          statement_total: 0,
          any_source_errors: false,
          has_draft_statement: false,
        }
        map.set(r.owner_id, g)
      }
      g.vehicles.push(r)
      g.vehicle_count += 1
      g.statement_total += r.statement_count
      if (r.any_source_errors) g.any_source_errors = true
      if (r.has_draft_statement) g.has_draft_statement = true
    }
    return Array.from(map.values()).sort((a, b) => a.owner_name.localeCompare(b.owner_name))
  }, [stateFilteredRows])

  // Text search over owner name, email, or any of the owner's plates.
  const owners = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return groups
    return groups.filter((g) => {
      const hay = [g.owner_name, g.owner_email, ...g.vehicles.map((v) => v.plate)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
      return hay.includes(term)
    })
  }, [groups, search])

  // Keep the selected owner valid as filters change; default to the first.
  useEffect(() => {
    if (owners.length === 0) {
      setSelectedOwnerId(null)
      return
    }
    setSelectedOwnerId((prev) =>
      prev && owners.some((o) => o.owner_id === prev) ? prev : owners[0]!.owner_id,
    )
  }, [owners])

  const selectedOwner = useMemo(
    () => owners.find((o) => o.owner_id === selectedOwnerId) ?? null,
    [owners, selectedOwnerId],
  )

  // Keep the selected vehicle valid within the current owner + filter.
  useEffect(() => {
    const vehicles = selectedOwner?.vehicles ?? []
    if (vehicles.length === 0) {
      setSelectedVehicleId(null)
      return
    }
    setSelectedVehicleId((prev) =>
      prev && vehicles.some((v) => v.vehicle_id === prev) ? prev : vehicles[0]!.vehicle_id,
    )
  }, [selectedOwner])

  const selectedVehicle = useMemo(
    () => selectedOwner?.vehicles.find((v) => v.vehicle_id === selectedVehicleId) ?? null,
    [selectedOwner, selectedVehicleId],
  )

  const handleSelectOwner = useCallback((ownerId: string) => {
    setSelectedOwnerId(ownerId)
    setSelectedVehicleId(null)
    setMobileStep('vehicles')
  }, [])

  const handleSelectVehicle = useCallback((vehicleId: string) => {
    setSelectedVehicleId(vehicleId)
    setMobileStep('statement')
  }, [])

  const handleResolved = useCallback(() => {
    triggerSummary({}, { skipCache: true })
    triggerIndex({}, { skipCache: true })
  }, [triggerSummary, triggerIndex])

  if (accessLoading) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }

  // Two different refusals, deliberately worded differently. "No cockpit access"
  // and "cockpit access without finance" are not the same situation, and an
  // operator who is told the wrong one asks the wrong person for help.
  if (!canView || !canViewFinance) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">
            {canView
              ? 'Owner Statements needs finance access.'
              : 'Owner Statements is available to admins only.'}
          </p>
          <p className="text-xs text-muted-foreground">
            {canView
              ? 'These pages show each owner’s earnings, costs, retention and payout. Ask an admin to add finance access if your work needs it.'
              : 'Contact an admin if you need access to owner statements.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ReceiptText className="h-5 w-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Owner Statements</h1>
          {view === 'owner' && (index.loading || summary.loading) && (
            <Spinner className="h-4 w-4 text-muted-foreground" />
          )}
          <div className="ml-auto">
            <ViewToggle view={view} onChange={changeView} />
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{caption}</p>
      </div>

      {/* Keyed so the two matrices never share filter state: React would
          otherwise reuse one instance across the switch, and a range chosen on
          the live tab would silently narrow the statements tab too. */}
      {view === 'month' ? (
        <StatementMatrix key="statements" />
      ) : view === 'live' ? (
        <StatementMatrix key="live" mode="live" />
      ) : view === 'evidence' ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <EvidenceCoverage />
        </div>
      ) : (
        <>
          {/* Fleet state toggle (mobile only) */}
          <button
            type="button"
            onClick={() => setFleetOpen((v) => !v)}
            className="flex min-h-[44px] w-full items-center justify-between gap-2 border-b border-border bg-card px-4 text-sm font-medium text-foreground lg:hidden"
          >
            <span>Fleet state</span>
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 transition-transform', fleetOpen && 'rotate-180')}
            />
          </button>

          {/* Fleet state strip: always visible at lg, toggled below lg */}
          <div className={cn(fleetOpen ? 'block' : 'hidden', 'lg:block')}>
            <FleetStateStrip
              counts={fleet.counts}
              reconciliation={fleet.reconciliation}
              resolutions={fleet.resolutions}
              selectedState={stateFilter}
              onSelectState={setStateFilter}
              loading={summary.loading}
              canEdit={canEditFleet}
              onResolved={handleResolved}
            />
          </div>

          {/* Mobile back bar: only shown once the operator has drilled in */}
          {mobileStep !== 'owners' && (
            <div className="flex items-center gap-2 border-b border-border bg-card px-2 py-1 lg:hidden">
              <button
                type="button"
                onClick={() =>
                  setMobileStep(mobileStep === 'statement' ? 'vehicles' : 'owners')
                }
                className="flex min-h-[44px] items-center gap-1 rounded-md px-2 text-sm font-medium text-foreground hover:bg-accent"
              >
                <ChevronLeft className="h-4 w-4 shrink-0" />
                Back
              </button>
              <div className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
                {mobileStep === 'vehicles' && selectedOwner && (
                  <span className="truncate font-medium text-foreground">
                    {selectedOwner.owner_name}
                  </span>
                )}
                {mobileStep === 'statement' && selectedOwner && (
                  <>
                    <span className="truncate font-medium text-foreground">
                      {selectedOwner.owner_name}
                    </span>
                    <ChevronRight className="h-4 w-4 shrink-0" />
                    <span className="truncate font-mono text-foreground">
                      {selectedVehicle?.plate ?? ''}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Master-detail: desktop uses collapsible/poppable panels, mobile drills
              down one pane at a time. Only one of the two trees is ever mounted. */}
          {isDesktop ? (
            <div className="flex min-h-0 flex-1 flex-row">
              <Panel id="owner-statements-owners" title="Owners" widthClass="w-80 shrink-0" floatWidth={420} floatHeight={640}>
                <OwnerStatementList
                  owners={owners}
                  loading={index.loading}
                  error={index.error}
                  search={search}
                  onSearchChange={setSearch}
                  selectedOwnerId={selectedOwnerId}
                  onSelectOwner={handleSelectOwner}
                />
              </Panel>
              {selectedOwner ? (
                <>
                  <Panel id="owner-statements-vehicles" title="Vehicles" widthClass="w-96 shrink-0" floatWidth={480} floatHeight={640}>
                    <VehicleList
                      ownerName={selectedOwner.owner_name}
                      vehicles={selectedOwner.vehicles}
                      selectedVehicleId={selectedVehicleId}
                      onSelectVehicle={handleSelectVehicle}
                    />
                  </Panel>
                  <Panel id="owner-statements-statement" title="Statement" widthClass="flex-1 min-w-0" floatWidth={960} floatHeight={720}>
                    <VehicleDetail vehicle={selectedVehicle} ownerName={selectedOwner.owner_name} />
                  </Panel>
                </>
              ) : (
                <div className="flex min-h-0 flex-1 items-center justify-center p-12 text-center text-sm text-muted-foreground">
                  {index.loading ? 'Loading…' : 'No owners match the current filters.'}
                </div>
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className={cn('min-h-0 flex-1 flex-col', mobileStep === 'owners' ? 'flex' : 'hidden')}>
                <OwnerStatementList
                  owners={owners}
                  loading={index.loading}
                  error={index.error}
                  search={search}
                  onSearchChange={setSearch}
                  selectedOwnerId={selectedOwnerId}
                  onSelectOwner={handleSelectOwner}
                />
              </div>
              {selectedOwner && (
                <>
                  <div
                    className={cn('min-h-0 flex-1 flex-col', mobileStep === 'vehicles' ? 'flex' : 'hidden')}
                  >
                    <VehicleList
                      ownerName={selectedOwner.owner_name}
                      vehicles={selectedOwner.vehicles}
                      selectedVehicleId={selectedVehicleId}
                      onSelectVehicle={handleSelectVehicle}
                    />
                  </div>
                  <div
                    className={cn('min-h-0 flex-1 flex-col', mobileStep === 'statement' ? 'flex' : 'hidden')}
                  >
                    <VehicleDetail vehicle={selectedVehicle} ownerName={selectedOwner.owner_name} />
                  </div>
                </>
              )}
              {!selectedOwner && mobileStep === 'owners' && owners.length === 0 && !index.loading && (
                <div className="flex min-h-0 flex-1 items-center justify-center p-12 text-center text-sm text-muted-foreground">
                  No owners match the current filters.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
